import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth/context/AuthContext';

export interface HealthCheckIn {
  id: string;
  client_id: string;
  mood_score: number;
  energy_score: number;
  sleep_hours: number;
  general_health_score: number;
  notes: string | null;
  check_in_date: string;
  created_at: string;
}

export interface HealthCheckInInput {
  mood_score: number;
  energy_score: number;
  sleep_hours: number;
  notes?: string;
}

interface HealthCheckInContextType {
  todayCheckIn: HealthCheckIn | null;
  history: HealthCheckIn[];
  isLoading: boolean;
  error: string | null;
  hasCheckedInToday: boolean;
  submitCheckIn: (data: HealthCheckInInput) => Promise<{ data?: HealthCheckIn; error: string | null }>;
  calculateTrend: () => { average: number; percentChange: number; direction: string } | null;
  getHealthStatus: (score: number) => { label: string; color: string; bgColor: string; textColor: string };
  refetch: () => Promise<void>;
}

const HealthCheckInContext = createContext<HealthCheckInContextType | undefined>(undefined);

export function HealthCheckInProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [todayCheckIn, setTodayCheckIn] = useState<HealthCheckIn | null>(null);
  const [history, setHistory] = useState<HealthCheckIn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCheckIns = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const today = new Date().toLocaleDateString('sv-SE');

      const { data: todayData, error: todayError } = await supabase
        .from('health_check_ins')
        .select('*')
        .eq('client_id', user.id)
        .eq('check_in_date', today)
        .maybeSingle();

      if (todayError) throw todayError;
      setTodayCheckIn(todayData);

      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { data: historyData, error: historyError } = await supabase
        .from('health_check_ins')
        .select('*')
        .eq('client_id', user.id)
        .gte('check_in_date', thirtyDaysAgo.toLocaleDateString('sv-SE'))
        .order('check_in_date', { ascending: false });

      if (historyError) throw historyError;
      setHistory(historyData || []);
    } catch (err: unknown) {
      console.error('Error fetching health check-ins:', err);
      setError(err instanceof Error ? err.message : 'Ett okänt fel uppstod');
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user) {
      setTodayCheckIn(null);
      setHistory([]);
      setIsLoading(false);
      return;
    }
    fetchCheckIns();
  }, [user?.id, fetchCheckIns]);

  const submitCheckIn = useCallback(async (data: HealthCheckInInput) => {
    if (!user) {
      return { error: 'Not authenticated' };
    }

    try {
      const today = new Date().toLocaleDateString('sv-SE');

      const { data: result, error } = await supabase
        .from('health_check_ins')
        .upsert(
          {
            client_id: user.id,
            mood_score: data.mood_score,
            energy_score: data.energy_score,
            sleep_hours: data.sleep_hours,
            notes: data.notes || null,
            check_in_date: today,
          },
          {
            onConflict: 'client_id,check_in_date',
          }
        )
        .select()
        .single();

      if (error) throw error;

      await fetchCheckIns();

      return { data: result, error: null };
    } catch (err: unknown) {
      console.error('Error submitting check-in:', err);
      return { error: err instanceof Error ? err.message : 'Ett okänt fel uppstod' };
    }
  }, [user, fetchCheckIns]);

  const calculateTrend = useCallback(() => {
    if (history.length < 2) return null;

    const last7Days = history.filter((h) => {
      const date = new Date(h.check_in_date);
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      return date >= sevenDaysAgo;
    });

    if (last7Days.length < 2) return null;

    const avg = last7Days.reduce((sum, h) => sum + (h.general_health_score || 0), 0) / last7Days.length;
    const oldestScore = last7Days[last7Days.length - 1].general_health_score;
    const newestScore = last7Days[0].general_health_score;

    const percentChange = ((newestScore - oldestScore) / oldestScore) * 100;

    return {
      average: avg,
      percentChange,
      direction: percentChange > 5 ? 'up' : percentChange < -5 ? 'down' : 'stable',
    };
  }, [history]);

  const getHealthStatus = useCallback((score: number) => {
    if (score >= 8) return { label: 'Utmärkt', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
    if (score >= 6) return { label: 'Bra', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' };
    if (score >= 4) return { label: 'Godtagbart', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
    return { label: 'Behöver uppmärksamhet', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
  }, []);

  return (
    <HealthCheckInContext.Provider
      value={{
        todayCheckIn,
        history,
        isLoading,
        error,
        hasCheckedInToday: !!todayCheckIn,
        submitCheckIn,
        calculateTrend,
        getHealthStatus,
        refetch: fetchCheckIns,
      }}
    >
      {children}
    </HealthCheckInContext.Provider>
  );
}

export function useHealthCheckInContext() {
  const context = useContext(HealthCheckInContext);
  if (context === undefined) {
    throw new Error('useHealthCheckInContext must be used within a HealthCheckInProvider');
  }
  return context;
}
